import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import publicInterviewService, { PublicInterviewConfig } from '../services/publicInterviewService';
import PublicInterviewPreview from '../components/interview-preview/PublicInterviewPreview';
import { ParticleBackground } from '../components/ParticleBackground';
import { PageTransitionItem } from '../utils/withTransition';
import { Button } from '../components/common/Button';
import { Navbar } from '../components/Navbar/Navbar';
import { CheckCircle, XCircle } from 'lucide-react';

const PublicInterviewPage: React.FC = () => {
  const { unique_id } = useParams<{ unique_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [config, setConfig] = useState<PublicInterviewConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showThankYou, setShowThankYou] = useState(false);

  const backgroundGradient = useMemo<React.CSSProperties>(() => ({
    background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
    position: 'relative',
    zIndex: 0,
  }), []);

  // Check if we're returning from completed interview
  const isReturningFromInterview = location.state?.interviewCompleted === true;

  console.log('[PublicInterviewPage] Current state:', { loading, error, showThankYou, hasConfig: !!config });

  const renderResultLayout = useCallback((content: React.ReactNode, showNavbar: boolean = true) => (
    <div
      className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark"
      style={backgroundGradient}
    >
      {showNavbar && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <Navbar
            showUserActions={false}
            showLogoText={true}
          />
        </div>
      )}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow" />
        <div
          className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow"
          style={{ animationDelay: '1s' }}
        />
        <div
          className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow"
          style={{ animationDelay: '2s' }}
        />
      </div>
      <ParticleBackground />
      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 pb-16">
        <PageTransitionItem index={1}>
          <div className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark p-8 max-w-lg w-full mx-4 text-center backdrop-blur-sm shadow-sm">
            {content}
          </div>
        </PageTransitionItem>
      </div>
    </div>
  ), [backgroundGradient]);

  useEffect(() => {
    // If returning from completed interview, show thank you screen
    if (isReturningFromInterview) {
      setShowThankYou(true);
      setLoading(false);
      // Update document title for completed interview
      document.title = 'Screening Interview Completed | JobProt';
      return;
    }

    const fetchConfig = async () => {
      if (!unique_id) {
        setError('Invalid interview link');
        setLoading(false);
        return;
      }

      try {
        const interviewConfig = await publicInterviewService.getInterviewConfig(unique_id);
        setConfig(interviewConfig);
        setLoading(false);
      } catch (err: any) {
        console.error('Error loading interview:', err);
        setError(err.message || 'Failed to load interview');
        setLoading(false);
      }
    };

    fetchConfig();
    
    // Cleanup: Reset document title when component unmounts
    return () => {
      document.title = 'MockTrail';
    };
  }, [unique_id, isReturningFromInterview]);

  const handleStartInterview = () => {
    console.log('Starting implementor interview:', unique_id);

    // Create mock interview details for public interview
    // This prevents InterviewPage from redirecting due to missing interviewDetailsFromState
    const mockInterviewDetails = {
      id: unique_id || '',
      userInterviewId: `public-${unique_id}`,
      title: config?.metadata?.interview_title || 'Interview',
      jobRole: config?.metadata?.position || 'Position',
      company: config?.company_details?.name || config?.metadata?.implementor_name || 'Company',
      companyLogo: config?.company_details?.photoUrl, // Changed from companyPhoto to companyLogo
      companyPhoto: config?.company_details?.photoUrl, // Keep for backward compatibility
      description: '',
      duration: 30,
      language: 'English',
      voicePack: 'Puck',
      characterGender: 'RANDOM',
      selectedResume: null,
      isPublicInterview: true,
      implementorUniqueId: unique_id,
      candidateName: config?.candidate?.name,
      candidatePhoto: config?.candidate?.photoUrl,
    };

    // Use navigate with state to pass interview details to InterviewPage
    // This mimics the normal flow where InterviewPage gets details from location.state
    navigate(`/screening-interview-active/${unique_id}`, {
      state: {
        interviewDetails: mockInterviewDetails,
        userInterviewId: `public-${unique_id}`,
        isPublicInterview: true,
        implementorUniqueId: unique_id,
        candidateName: config?.candidate?.name,
        candidatePhoto: config?.candidate?.photoUrl,
      },
      replace: true,
    });
  };


  if (loading) {
    return renderResultLayout(
      <>
        <div className="mx-auto mb-6 h-14 w-14 animate-spin rounded-full border-4 border-primary-light/20 border-t-primary-light dark:border-primary-dark/20 dark:border-t-primary-dark" />
        <h1 className="text-xl font-semibold mb-3">Preparing your interview…</h1>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          Hang tight while we set up your interview experience.
        </p>
      </>,
      true
    );
  }

  if (error) {
    return renderResultLayout(
      <>
        <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-12 h-12 text-red-600 dark:text-red-400" />
        </div>
        <h1 className="text-2xl font-semibold mb-3">Interview Not Found</h1>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-6">
          {'The interview link may be invalid or has expired. Please contact the team that shared it for a fresh link.'}
        </p>
        <div className="flex justify-center gap-3">
          <Button variant="secondary" onClick={() => window.open('https://platform.mocktrail.app', '_blank', 'noopener,noreferrer')}>
            Go to Dashboard
          </Button>
        </div>
      </>,
      true
    );
  }

  if (showThankYou) {
    return renderResultLayout(
      <>
        <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
        </div>
        <h1 className="text-2xl font-semibold mb-3">Thank You!</h1>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-6">
          Your interview has been <strong className='text-text-primary-light dark:text-text-primary-dark'>completed successfully</strong>.<br />
          Feedback will be sent to the hiring team shortly.
        </p>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          You may now close this window.
        </p>
      </>,
      true
    );
  }

  // Show interview preview screen
  if (!config) {
    console.log('[PublicInterviewPage] No config, showing loading...');
    return renderResultLayout(
      <>
        <div className="mx-auto mb-6 h-14 w-14 animate-spin rounded-full border-4 border-primary-light/20 border-t-primary-light dark:border-primary-dark/20 dark:border-t-primary-dark" />
        <h1 className="text-xl font-semibold mb-3">Loading interview details…</h1>
      </>,
      true
    );
  }

  console.log('[PublicInterviewPage] Rendering PublicInterviewPreview with config:', config);
  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark">
      <PublicInterviewPreview
        companyName={config?.company_details?.name || config?.metadata?.implementor_name || 'Company'}
        companyPhoto={config?.company_details?.photoUrl}
        candidateName={config?.candidate?.name || 'Guest'}
        candidatePhoto={config?.candidate?.photoUrl}
        interviewTitle={config?.metadata?.interview_title}
        interviewPosition={config?.metadata?.position}
        onStartInterview={handleStartInterview}
        expiresAt={config?.expires_at || new Date().toISOString()}
      />
    </div>
  );
};

export default PublicInterviewPage;
